import * as Cesium from "cesium";

export function createMeasureDistanceTool(viewer, { onFinish } = {}) {
  function getCartesianFromScreenPosition(viewer, windowPosition) {
    const { scene } = viewer;
    if (!scene) return undefined;

    if (scene.pickPositionSupported) {
      const picked = scene.pickPosition(windowPosition);
      if (Cesium.defined(picked)) return picked;
    }

    const ray = viewer.camera.getPickRay(windowPosition);
    if (Cesium.defined(ray)) {
      const globePos = scene.globe.pick(ray, scene);
      if (Cesium.defined(globePos)) return globePos;
    }

    const ellipsoidPos = viewer.camera.pickEllipsoid(windowPosition, scene.globe.ellipsoid);
    if (Cesium.defined(ellipsoidPos)) return ellipsoidPos;

    return undefined;
  }

  const sumDistance = (positions) => {
    let total = 0;
    for (let i = 0; i < positions.length - 1; i++) {
      total += Cesium.Cartesian3.distance(positions[i], positions[i + 1]);
    }
    return total;
  };

  const entities = [];
  const positions = [];
  let tempMousePosition = null;
  let labelEntity = null;
  let paused = false; // Toggle drawing with right click

  // Disable default double click zoom (and restore on destroy)
  const widgetHandler = viewer.cesiumWidget.screenSpaceEventHandler;
  const prevDblClick = widgetHandler.getInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  widgetHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  const addEntity = (e) => {
    entities.push(e);
    return e;
  };

  // Polyline (dynamic)
  addEntity(
    viewer.entities.add({
      polyline: {
        positions: new Cesium.CallbackProperty(() => {
          if (positions.length === 0) return [];
          if (paused) return [...positions];
          return tempMousePosition ? [...positions, tempMousePosition] : [...positions];
        }, false),
        width: 3,
        material: new Cesium.PolylineDashMaterialProperty({
          color: Cesium.Color.fromCssColorString("#CDBCF4"),
        }),
        clampToGround: true,
      },
    }),
  );

  // Live hover label
  const liveLabel = addEntity(
    viewer.entities.add({
      position: new Cesium.CallbackProperty(() => (paused ? undefined : tempMousePosition), false),
      label: {
        text: "",
        font: "16px sans-serif",
        fillColor: Cesium.Color.BLACK,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        outlineWidth: 1,
        outlineColor: Cesium.Color.BLACK,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(10, -10),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    }),
  );

  // Left click: add points
  handler.setInputAction((click) => {
    if (paused) return;

    const cartesian = getCartesianFromScreenPosition(viewer, click.position);
    if (!Cesium.defined(cartesian)) return;

    positions.push(cartesian);

    addEntity(
      viewer.entities.add({
        position: cartesian,
        point: {
          pixelSize: 7,
          color: Cesium.Color.fromCssColorString("#471AAF"),
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2,
        },
      }),
    );

    if (positions.length === 1) {
      labelEntity = addEntity(
        viewer.entities.add({
          position: positions[0],
          label: {
            text: "0.00 m",
            font: "16px sans-serif",
            fillColor: Cesium.Color.BLACK,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            outlineWidth: 2,
            outlineColor: Cesium.Color.BLACK,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -25),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        }),
      );
    }

    if (labelEntity) {
      labelEntity.label.text = `${sumDistance(positions).toFixed(2)} m`;
    }
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  // Mouse move: live readout
  handler.setInputAction((movement) => {
    if (paused) return;

    const cartesian = getCartesianFromScreenPosition(viewer, movement.endPosition);
    if (!Cesium.defined(cartesian)) return;

    tempMousePosition = cartesian;

    if (positions.length > 0) {
      const lastPos = positions[positions.length - 1];
      const liveDistance = Cesium.Cartesian3.distance(lastPos, tempMousePosition);
      const total = sumDistance(positions);
      liveLabel.label.text = `${liveDistance.toFixed(2)} m\nTotal: ${(total + liveDistance).toFixed(2)} m`;
    } else {
      liveLabel.label.text = "";
    }
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  const finish = () => onFinish?.();

  const handleEsc = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      finish();
    }
  };

  window.addEventListener("keydown", handleEsc, { capture: true });

  // Right click: toggle pause/resume (do NOT finish/destroy)
  handler.setInputAction(() => {
    paused = !paused;

    // Freeze dynamic segment and hide live label when paused
    if (paused) {
      tempMousePosition = null;
      liveLabel.label.text = "";
    }
  }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

  // Keep double click behavior as before (finish)
  handler.setInputAction(finish, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

  return {
    destroy() {
      for (const e of entities) viewer.entities.remove(e);
      try {
        handler.destroy();
      } catch {}

      window.removeEventListener("keydown", handleEsc, { capture: true });

      widgetHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
      if (prevDblClick) widgetHandler.setInputAction(prevDblClick, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    },
  };
}
